import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Tag, ShieldCheck, CreditCard, Gift } from "lucide-react";
import { api, inr, tierLabel } from "@/lib/api";
import { OffersList } from "@/components/OffersList";
import { useStore } from "@/context/StoreContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const empty = { name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" };

export default function Checkout() {
  const { cart, user, setAuthOpen, clearCart, config } = useStore();
  const nav = useNavigate();
  const [addr, setAddr] = useState(empty);
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [quote, setQuote] = useState(null);
  const [notifyWa, setNotifyWa] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [email, setEmail] = useState(user?.email || "");
  const [giftWrap, setGiftWrap] = useState(false);
  const [giftRecipient, setGiftRecipient] = useState("");
  const [giftMessage, setGiftMessage] = useState("");

  const cartItems = useMemo(() => cart.map((i) => ({ product_id: i.product_id, pieces: i.pieces, qty: i.qty })), [cart]);

  const refreshQuote = useCallback((code, wrap) => {
    if (cart.length === 0) return;
    api.post("/checkout/quote", {
      items: cartItems,
      coupon: code ?? appliedCoupon,
      gift_wrap: wrap ?? giftWrap,
    }).then((r) => setQuote(r.data));
  }, [cart.length, cartItems, appliedCoupon, giftWrap]);

  useEffect(() => { refreshQuote(); }, [refreshQuote, user]);
  useEffect(() => { if (user) { setAddr((a) => ({ ...a, name: a.name || user.name || "", phone: a.phone || user.phone || "" })); setEmail(user.email || ""); } }, [user]);

  const [couponError, setCouponError] = useState("");
  const applyCoupon = async (codeArg) => {
    const code = (typeof codeArg === "string" ? codeArg : coupon).trim();
    if (!code) return;
    if (typeof codeArg === "string") setCoupon(code);
    try {
      const { data } = await api.post("/checkout/quote", { items: cartItems, coupon: code, gift_wrap: giftWrap });
      if (data.coupon_error) { setCouponError(data.coupon_error); return toast.error(data.coupon_error); }
      setCouponError("");
      setAppliedCoupon(data.coupon_code);
      setQuote(data);
      toast.success(`Promo ${data.coupon_code} applied — you save ${inr(data.discount)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply promo code");
    }
  };
  const removeCoupon = () => { setAppliedCoupon(null); setCoupon(""); setCouponError(""); refreshQuote(""); };
  useEffect(() => {
    if (appliedCoupon && quote?.coupon_error) { toast.error(quote.coupon_error); setAppliedCoupon(null); setCouponError(quote.coupon_error); }
  }, [quote]); // eslint-disable-line

  const valid = addr.name && addr.phone && addr.line1 && addr.city && addr.state && addr.pincode;

  const finalize = async (orderId, rp) => {
    try {
      await api.post("/checkout/verify-payment", { order_id: orderId, ...rp });
      clearCart();
      toast.success("Payment successful!");
      nav(`/order/${orderId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment verification failed");
    } finally { setPlacing(false); }
  };

  const placeOrder = async () => {
    if (!user) { setAuthOpen(true); return toast.message("Please sign in to complete your order"); }
    if (!valid) return toast.error("Please complete your delivery details");
    setPlacing(true);
    try {
      const { data } = await api.post("/checkout/create-order", {
        items: cartItems, coupon: appliedCoupon,
        address: { ...addr, is_default: false },
        email: email || undefined, phone: addr.phone, notify_whatsapp: notifyWa,
        gift_wrap: giftWrap, gift_message: giftMessage, gift_recipient: giftRecipient,
      });
      const orderId = data.order.order_id;

      if (data.configured && data.key_id) {
        const ok = await loadRazorpay();
        if (!ok) { setPlacing(false); return toast.error("Could not load payment gateway"); }
        const rz = new window.Razorpay({
          key: data.key_id, amount: data.amount, currency: data.currency,
          name: "SCHOKODALE", description: "The Valley of Chocolates",
          order_id: data.razorpay_order_id,
          prefill: { name: data.customer.name, email: data.customer.email, contact: data.customer.phone },
          theme: { color: "#D4AF37" },
          handler: (resp) => finalize(orderId, {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          }),
          modal: { ondismiss: () => { setPlacing(false); toast.message("Payment cancelled"); } },
        });
        rz.open();
      } else {
        // Razorpay keys not yet configured — simulate a successful capture (DEMO)
        toast.message("Demo payment", { description: "Razorpay keys not yet added — simulating a successful payment." });
        setTimeout(() => finalize(orderId, {}), 1200);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order");
      setPlacing(false);
    }
  };

  if (cart.length === 0) {
    return <div className="py-32 text-center text-[#8C7B6E]" data-testid="checkout-empty">Your cart is empty. <a href="/shop" className="text-[#D4AF37]">Shop now</a></div>;
  }

  const field = (key, label, span) => (
    <div className={span ? "sm:col-span-2" : ""}>
      <label className="text-xs text-[#8C7B6E] uppercase tracking-wider">{label}</label>
      <Input data-testid={`addr-${key}`} value={addr[key]} onChange={(e) => setAddr({ ...addr, [key]: e.target.value })}
        className="mt-1 bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7]" />
    </div>
  );

  return (
    <div data-testid="checkout-page" className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
      <h1 className="font-display text-4xl sm:text-5xl mb-8">Checkout</h1>
      <div className="grid lg:grid-cols-[1fr_380px] gap-10">
        <div className="space-y-6">
          {!user && (
            <div className="lux-card p-5 border-[#D4AF37]/40 flex items-center justify-between" data-testid="checkout-login-notice">
              <p className="text-[#C8B8AB] text-sm">Sign in to save this order to your account history.</p>
              <Button onClick={() => setAuthOpen(true)} className="btn-gold rounded-full" data-testid="checkout-signin-btn">Sign In</Button>
            </div>
          )}

          <div className="lux-card p-6">
            <h3 className="font-display text-xl mb-5">Delivery Details</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {field("name", "Full Name")}
              {field("phone", "Mobile Number")}
              <div className="sm:col-span-2">
                <label className="text-xs text-[#8C7B6E] uppercase tracking-wider">Email (for invoice)</label>
                <Input data-testid="addr-email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7]" />
              </div>
              {field("line1", "Address Line 1", true)}
              {field("line2", "Address Line 2 (optional)", true)}
              {field("city", "City")}
              {field("state", "State")}
              {field("pincode", "Pincode")}
            </div>
            <div className="flex items-center justify-between mt-5 pt-5 border-t border-[#3B251D]">
              <span className="text-sm text-[#C8B8AB]">Send order updates on WhatsApp</span>
              <Switch checked={notifyWa} onCheckedChange={setNotifyWa} data-testid="notify-whatsapp-switch" />
            </div>
          </div>

          <div className="lux-card p-6" data-testid="gift-wrap-section">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl flex items-center gap-2"><Gift size={18} className="text-[#D4AF37]" /> Make it a Gift</h3>
              <Switch checked={giftWrap} onCheckedChange={setGiftWrap} data-testid="gift-wrap-switch" />
            </div>
            <p className="text-sm text-[#8C7B6E] mt-2">Signature gold gift-wrap with a message card — just {inr(149)}.</p>
            {giftWrap && (
              <div className="mt-5 grid gap-3 animate-fade-up">
                <div>
                  <label className="text-xs text-[#8C7B6E] uppercase tracking-wider">Recipient name (optional)</label>
                  <Input data-testid="gift-recipient" value={giftRecipient} onChange={(e) => setGiftRecipient(e.target.value)} placeholder="e.g. Priya"
                    className="mt-1 bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7]" />
                </div>
                <div>
                  <label className="text-xs text-[#8C7B6E] uppercase tracking-wider">Gift message card</label>
                  <Textarea data-testid="gift-message" maxLength={300} value={giftMessage} onChange={(e) => setGiftMessage(e.target.value)}
                    placeholder="Write a warm note to accompany the chocolates…" className="mt-1 bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7]" />
                  <p className="text-[10px] text-[#8C7B6E] text-right mt-1">{giftMessage.length}/300</p>
                </div>
              </div>
            )}
          </div>

          <div className="lux-card p-6">
            <h3 className="font-display text-xl mb-4 flex items-center gap-2"><CreditCard size={18} className="text-[#D4AF37]" /> Payment</h3>
            <p className="text-sm text-[#C8B8AB]">Secure payment via Razorpay — UPI, Credit/Debit Cards & Net Banking.</p>
            {!config.razorpay_configured && (
              <p className="text-xs text-[#D97706] mt-2" data-testid="payment-demo-note">Demo mode: live Razorpay keys not yet added. Payment will be simulated.</p>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="lux-card p-6 h-max lg:sticky lg:top-28">
          <h3 className="font-display text-xl mb-5">Order Summary</h3>
          <div className="space-y-3 max-h-56 overflow-auto mb-4 pr-1">
            {quote?.items.map((it) => (
              <div key={`${it.product_id}-${it.pieces}`} className="flex justify-between text-sm">
                <span className="text-[#C8B8AB]">{it.name} · {tierLabel(it.pieces)} × {it.qty}{it.product_discount > 0 && <span className="ml-1 text-[10px] text-[#D4AF37]">−{it.product_discount}%</span>}</span>
                <span className="text-[#FDFBF7]">{inr(it.line_total)}</span>
              </div>
            ))}
          </div>

          {appliedCoupon ? (
            <div className="flex items-center justify-between bg-[#2E7D32]/10 border border-[#2E7D32]/40 rounded-lg px-3 py-2.5 mb-4" data-testid="coupon-applied">
              <span className="text-sm text-[#7BC47F] flex items-center gap-2"><Tag size={14} /> {appliedCoupon} applied</span>
              <button data-testid="remove-coupon-btn" onClick={removeCoupon} className="text-xs text-[#C8B8AB] hover:text-[#DC2626]">Remove</button>
            </div>
          ) : (
            <>
              <div className="flex gap-2 mb-2">
                <div className="flex items-center bg-[#1A0F0B] border border-[#3B251D] rounded-lg px-3 flex-1">
                  <Tag size={14} className="text-[#8C7B6E]" />
                  <input data-testid="coupon-input" value={coupon} onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setCouponError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && applyCoupon()} placeholder="Promo code"
                    className="bg-transparent outline-none text-sm text-[#FDFBF7] ml-2 w-full py-2.5" />
                </div>
                <Button data-testid="apply-coupon-btn" onClick={applyCoupon} className="btn-outline-gold rounded-lg">Apply</Button>
              </div>
              {couponError && <p className="text-xs text-[#E57373] mb-2" data-testid="coupon-error">{couponError}</p>}
              <p className="text-[10px] text-[#8C7B6E] mb-4">Promo codes apply only to items that are not already discounted.</p>
            </>
          )}
          <OffersList items={cartItems} giftWrap={giftWrap} applied={appliedCoupon} onApply={applyCoupon}
            refreshKey={`${JSON.stringify(cartItems)}-${user?.user_id || ""}-${appliedCoupon || ""}`} />

          <div className="space-y-2 border-t border-[#3B251D] pt-4">
            <div className="flex justify-between text-[#C8B8AB] text-sm"><span>Subtotal</span><span data-testid="summary-subtotal">{inr(quote?.subtotal || 0)}</span></div>
            {quote?.mrp_total > quote?.subtotal && <div className="flex justify-between text-[#D4AF37] text-sm"><span>Product offers (already applied)</span><span data-testid="summary-product-savings">−{inr(quote.mrp_total - quote.subtotal)}</span></div>}
            {quote?.discount > 0 && <div className="flex justify-between text-[#2E7D32] text-sm"><span>Promo ({quote.coupon_code})</span><span data-testid="summary-discount">−{inr(quote.discount)}</span></div>}
            <div className="flex justify-between text-[#C8B8AB] text-sm"><span>Delivery</span><span data-testid="summary-delivery">{quote?.free_delivery ? "Free" : inr(quote?.delivery_fee || 0)}</span></div>
            {quote?.gift_wrap && <div className="flex justify-between text-[#C8B8AB] text-sm"><span>Gift wrap & card</span><span data-testid="summary-giftwrap">{inr(quote?.gift_wrap_fee || 0)}</span></div>}
          </div>
          <div className="flex justify-between font-display text-2xl border-t border-[#3B251D] mt-4 pt-4">
            <span>Total</span><span className="text-[#D4AF37]" data-testid="summary-total">{inr(quote?.total || 0)}</span>
          </div>

          <Button data-testid="place-order-btn" onClick={placeOrder} disabled={placing} className="btn-gold w-full h-14 rounded-full mt-6">
            {placing ? <Loader2 className="animate-spin" size={18} /> : `Pay ${inr(quote?.total || 0)}`}
          </Button>
          <p className="flex items-center justify-center gap-1 text-xs text-[#8C7B6E] mt-3"><ShieldCheck size={13} /> 256-bit secured checkout · Prepaid only (no COD)</p>
          <p className="text-[11px] text-[#8C7B6E] mt-2 text-center" data-testid="checkout-policy-note">By paying you agree to our <a href="/terms-and-conditions" className="text-[#D4AF37] underline">Terms</a> and <a href="/refund-cancellation" className="text-[#D4AF37] underline">Refund & Cancellation Policy</a>. Free cancellation within 6 hours.</p>
        </div>
      </div>
    </div>
  );
}
